/**
 * GLSL code and material creation for stroked wide lines.
 * @module
 */
import stableHash from "common/utils/stableHash";
import * as THREE from "three";
import { defaultStencilProperties } from "../defaultStencilProperties";
import type { HighlightDataTexture } from "../HighlightDataTexture";
import { includeEntityHighlight } from "./customizeShader";

export class MeshBasicMaterialFactory {
  private materials: { [key: string]: THREE.MeshBasicMaterial } = {};

  private static _instance: MeshBasicMaterialFactory | undefined;

  static get instance(): MeshBasicMaterialFactory {
    return this._instance ?? (this._instance = new MeshBasicMaterialFactory());
  }

  private highlightDataTextureUniform: THREE.IUniform = { value: null };
  private isolateModeUniform: THREE.IUniform = { value: false };

  private constructor() {}

  getMaterial(options: Partial<THREE.MeshBasicMaterialParameters>): THREE.MeshBasicMaterial {
    const usedOptions: Partial<THREE.MeshBasicMaterialParameters> = {
      ...defaultStencilProperties,
      ...options,
      name: "MeshBasicMaterialFactory material",
    };
    const hash = stableHash(usedOptions); // TODO normalize color
    if (this.materials[hash]) {
      return this.materials[hash];
    }

    const material = (this.materials[hash] = new THREE.MeshBasicMaterial(usedOptions));
    material.defines = { ISOLATE_MODE: 1 };

    material.onBeforeCompile = (shader) => {
      const { uniforms } = shader;

      uniforms.highlightedIds = this.highlightDataTextureUniform;
      uniforms.isolateMode = this.isolateModeUniform;

      return includeEntityHighlight(shader);
    };

    return material;
  }

  prepareRender(highlightDataTexture?: HighlightDataTexture) {
    this.highlightDataTextureUniform.value = highlightDataTexture?.texture ?? null;
    this.isolateModeUniform.value = highlightDataTexture?.isolateMode ?? false;
  }
}
